import React from 'react';
import { Layers, ArrowUpRight } from 'lucide-react';
import styles from './DashboardKPICards.module.css';

interface SupranationalEntry {
    code: string;
    name: string;
    count: number;
}

interface DashboardKPICardsProps {
    totalJurisdictions: number | null;
    totalRegulations: number | null;
    countriesTracked: number | null;
    supranationalEntries: SupranationalEntry[];
    onActivePoliciesClick: () => void;
    onCountriesClick?: () => void;
}

const fmt = (n: number | null) => (n !== null ? n.toLocaleString() : '—');

const DashboardKPICards: React.FC<DashboardKPICardsProps> = ({
    totalJurisdictions,
    totalRegulations,
    countriesTracked,
    supranationalEntries,
    onActivePoliciesClick,
    onCountriesClick,
}) => {
    const supranationalCodes = supranationalEntries.length
        ? supranationalEntries.map(s => s.code).join(' · ')
        : 'No data yet';

    return (
        <div className={styles.panel} style={{ margin: '0 0 1.35rem 0' }}>
            <div className={styles.panelTop}>
                <span className={styles.iconMark}>
                    <Layers size={20} aria-hidden strokeWidth={1.6} />
                </span>
                <span className={styles.eyebrow}>Regulatory Monitoring</span>
            </div>

            <div
                className={styles.clickable}
                onClick={onActivePoliciesClick}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivePoliciesClick(); } }}
                title="View breakdown by country and jurisdiction"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}
            >
                <div className={styles.hero}>
                    <span className={styles.heroValue}>{fmt(totalRegulations)}</span>
                    <span className={styles.heroCaption}>active policies tracked worldwide</span>
                    <span className={styles.viewLink} style={{ marginLeft: 'auto' }}>
                        View breakdown <ArrowUpRight size={15} aria-hidden />
                    </span>
                </div>
                <p className={styles.heroSub}>
                    Live regulatory coverage across every monitored jurisdiction — click to explore the
                    full breakdown by country, jurisdiction, and policy type.
                </p>
            </div>

            <div className={styles.statStrip}>
                <div className={styles.stat}>
                    <div className={styles.statValueRow}>
                        <span className={styles.statValue}>{fmt(totalJurisdictions)}</span>
                    </div>
                    <span className={styles.statLabel}>Jurisdictions</span>
                    <span className={styles.statMeta}>Included in system</span>
                </div>

                <div
                    className={`${styles.stat} ${onCountriesClick ? styles.statInteractive : ''}`}
                    onClick={onCountriesClick}
                    title={onCountriesClick ? 'View breakdown by country and jurisdiction' : undefined}
                >
                    <div className={styles.statValueRow}>
                        <span className={styles.statValue}>{fmt(countriesTracked)}</span>
                        {onCountriesClick && <ArrowUpRight className={styles.statArrow} size={15} aria-hidden />}
                    </div>
                    <span className={styles.statLabel}>Countries Covered</span>
                    <span className={styles.statMeta}>Distinct countries</span>
                </div>

                <div className={styles.stat} title={supranationalEntries.map(s => `${s.name} (${s.count})`).join(', ')}>
                    <div className={styles.statValueRow}>
                        <span className={styles.statValue}>{supranationalEntries.length || '—'}</span>
                    </div>
                    <span className={styles.statLabel}>Supranationals</span>
                    <span className={styles.statMeta}>{supranationalCodes}</span>
                </div>
            </div>
        </div>
    );
};

export default DashboardKPICards;
